const readCookieValue = (cookieName: string) => {
  if (typeof document === 'undefined') return undefined;
  const cookies = document.cookie.split(';');
  const cookie = cookies.find((item) =>
    item.trim().startsWith(`${cookieName}=`)
  );
  return cookie ? cookie.split('=')[1] : undefined;
};

const readFirstAvailable = (keys: string[]) => {
  if (typeof window === 'undefined') return undefined;

  const params = new URLSearchParams(window.location.search);
  for (const key of keys) {
    const value = params.get(key);
    if (value) {
      sessionStorage.setItem(key, value);
      return value;
    }
  }

  for (const key of keys) {
    const value = sessionStorage.getItem(key);
    if (value) {
      return value;
    }
  }

  return undefined;
};

export const syncUrlParamsToSessionStorage = () => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  params.forEach((value, key) => {
    if (!value) return;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, value);
    }
    // Persist UTM params to localStorage for cross-session tracking
    if (key.toLowerCase().startsWith('utm_')) {
      localStorage.setItem(key.toLowerCase(), value);
    }
  });

  // Mirror common Facebook aliases so downstream callers can use one accessor.
  const fbClickId = params.get('fbc') || params.get('fbclickid') || params.get('fbclid');
  if (fbClickId) {
    sessionStorage.setItem('fbc', fbClickId);
    sessionStorage.setItem('fbclickid', fbClickId);
    if (params.get('fbclid')) {
      sessionStorage.setItem('fbclid', params.get('fbclid') as string);
    }
  }

  const fbBrowserId = params.get('fbp') || params.get('fbbrowserid');
  if (fbBrowserId) {
    sessionStorage.setItem('fbp', fbBrowserId);
    sessionStorage.setItem('fbbrowserid', fbBrowserId);
  }

  const fbLoginId = params.get('fb_login_id') || params.get('fbloginid');
  if (fbLoginId) {
    sessionStorage.setItem('fb_login_id', fbLoginId);
    sessionStorage.setItem('fbloginid', fbLoginId);
  }
};

export type FBAdvancedMatching = {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ge?: string;
  db?: string;
  ct?: string;
  st?: string;
  zp?: string;
  country?: string;
  external_id?: string;
};

const normalizeLetters = (value?: string) =>
  value?.trim().toLowerCase().replace(/[^a-z]/g, '');

const normalizeCompact = (value?: string) =>
  value?.trim().toLowerCase().replace(/\s+/g, '');

const normalizeDigits = (value?: string) => value?.replace(/\D/g, '');

const buildAdvancedMatchingPayload = (advancedMatching?: FBAdvancedMatching) => {
  if (!advancedMatching) return null;

  const email = advancedMatching.em?.trim().toLowerCase();
  const phone = normalizeDigits(advancedMatching.ph);
  const firstName = normalizeLetters(advancedMatching.fn);
  const lastName = normalizeLetters(advancedMatching.ln);
  const gender = normalizeLetters(advancedMatching.ge)?.slice(0, 1);
  const birthDate = normalizeDigits(advancedMatching.db);
  const city = normalizeCompact(advancedMatching.ct);
  const state = normalizeCompact(advancedMatching.st);
  const zip = normalizeCompact(advancedMatching.zp);
  const country = normalizeCompact(advancedMatching.country);
  const externalId = advancedMatching.external_id?.trim();

  const payload = {
    ...(email ? { em: email } : {}),
    ...(phone ? { ph: phone } : {}),
    ...(firstName ? { fn: firstName } : {}),
    ...(lastName ? { ln: lastName } : {}),
    ...(gender === 'f' || gender === 'm' ? { ge: gender } : {}),
    ...(birthDate ? { db: birthDate } : {}),
    ...(city ? { ct: city } : {}),
    ...(state ? { st: state } : {}),
    ...(zip ? { zp: zip } : {}),
    ...(country ? { country } : {}),
    ...(externalId ? { external_id: externalId } : {}),
  };

  return Object.keys(payload).length > 0 ? payload : null;
};

export const initializeFBPixel = (
  pixelId: string | number,
  advancedMatching?: FBAdvancedMatching
) => {
  const pixelIdLiteral = JSON.stringify(String(pixelId));
  const advancedMatchingPayload =
    buildAdvancedMatchingPayload(advancedMatching);
  const advancedMatchingLiteral = advancedMatchingPayload
    ? `, ${JSON.stringify(advancedMatchingPayload)}`
    : '';

  // Create and add the Facebook Pixel script
  const script = document.createElement('script');
  script.innerHTML = `
      !function(f,b,e,v,n,t,s) {
        if(f.fbq)return;
        n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)
      }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', ${pixelIdLiteral}${advancedMatchingLiteral});
      fbq('track', 'PageView');
    `;
  document.head.appendChild(script);

  // Add the noscript pixel image for browsers with JavaScript disabled
  /*const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);*/
};

export const initializeGtm = (googleId) => {
  // Add the async Google Tag Manager script
  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleId}`;
  document.head.appendChild(gtmScript);

  // Add the inline configuration script
  const configScript = document.createElement('script');
  configScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${googleId}');
      
    `;

  document.head.appendChild(configScript);
};

export const getFbClickId = () => {
  return (
    readFirstAvailable(['fbc', 'fbclickid', 'fbclid']) ||
    readCookieValue('_fbc')
  );
};

export const getFbBrowserId = () => {
  return (
    readFirstAvailable(['fbp', 'fbbrowserid']) ||
    readCookieValue('_fbp')
  );
};

export const getFbLoginId = () => {
  return readFirstAvailable(['fb_login_id', 'fbloginid']);
};
